/**
 * Copy text without ever throwing or leaving a rejected promise behind.
 *
 * `navigator.clipboard.writeText` rejects with `NotAllowedError` when a
 * permissions policy blocks the Clipboard API (a checkout embedded in a
 * creator's iframe without `allow="clipboard-write"`, some in-app browsers) —
 * an unhandled rejection that Sentry reported as FRONTEND-71. Fall back to the
 * legacy `execCommand("copy")` path, and report whether anything was copied so
 * the caller only shows "Copied!" when it is true.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
	try {
		if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text);
			return true;
		}
	} catch {
		// Blocked by a permissions policy / document not focused — fall through.
	}
	return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
	if (typeof document === "undefined" || !document.body) return false;
	const textarea = document.createElement("textarea");
	textarea.value = text;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "fixed";
	textarea.style.top = "0";
	textarea.style.left = "0";
	textarea.style.opacity = "0";
	document.body.appendChild(textarea);
	try {
		textarea.select();
		return typeof document.execCommand === "function"
			? document.execCommand("copy")
			: false;
	} catch {
		return false;
	} finally {
		textarea.remove();
	}
}
